import React, { useCallback, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import type { MessageDescriptor } from 'react-intl-next';

import { cx, cssMap } from '@atlaskit/css';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Text } from '@atlaskit/primitives/compiled/text';
import Toggle from '@atlaskit/toggle/toggle';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { token } from '@atlaskit/tokens';

import { getTabColorAppearance, type TabColorKey } from './tabColorDefinitions.ts';
import type { TabColorPalette } from './tabColorPalettes.ts';

const i18n = defineMessages({
	noColor: {
		id: 'native-tabs.tab-color-picker.no-color.non-final',
		defaultMessage: 'No color',
		description: 'Aria label for the swatch that removes tab color (resets to default).',
	},
	selectedSuffix: {
		id: 'native-tabs.tab-color-picker.selected-suffix.non-final',
		defaultMessage: '{colorLabel} (selected)',
		description: 'Aria label suffix when a color swatch is currently selected.',
	},
	colorPickerLabel: {
		id: 'native-tabs.tab-color-picker.label.non-final',
		defaultMessage: 'Tab color',
		description: 'Accessible label for the color picker radiogroup.',
	},
	applyToAll: {
		id: 'native-tabs.tab-color-picker.apply-to-all.non-final',
		defaultMessage: 'Apply color to all tabs',
		description: 'Toggle label to apply the current tab color to all tabs.',
	},
});

export type TabColorPickerProps = {
	/** The currently selected color key, or undefined if no color is set. */
	currentColor: TabColorKey | undefined;
	/** Called when the user selects a swatch. `null` means "remove color". */
	onColorSelect: (color: TabColorKey | null) => void;
	/** Called with the element that should receive initial focus when the picker opens. */
	setInitialFocusRef?: (el: HTMLElement | null) => void;
	/** Ready-to-render palette configuration selected by the feature boundary. */
	palette: TabColorPalette;
	/** Whether "Apply color to all tabs" toggle is on. */
	applyToAll?: boolean;
	/** Called when the user toggles "Apply color to all tabs". */
	onApplyToAllChange?: (isChecked: boolean) => void;
};

type TabColorPickerGridCustomProperties = React.CSSProperties & {
	'--tab-color-picker-column-count': number;
};

// The style prop assigns runtime values to these variables; cssMap consumes them below.
type TabColorSwatchCustomProperties = React.CSSProperties & {
	'--tab-color-swatch-background-color'?: string;
};

const styles = cssMap({
	grid: {
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
		display: 'grid',
		gap: token('space.075'),
		gridTemplateColumns: 'repeat(var(--tab-color-picker-column-count), 24px)',
	},
	swatch: {
		width: '24px',
		height: '24px',
		borderRadius: token('radius.small'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		padding: '0',
		cursor: 'pointer',
		flexShrink: 0,
	},
	swatchNoColor: {
		backgroundColor: token('elevation.surface'),
	},
	swatchFromCatalog: {
		// @ts-expect-error -- cssMap does not support runtime CSS variables for colors.
		backgroundColor: 'var(--tab-color-swatch-background-color)',
	},
	actionsDivider: {
		paddingBlock: token('space.025'),
		paddingInlineStart: token('space.150'),
		paddingInlineEnd: token('space.100'),
	},
});

type SwatchProps = {
	colorKey: TabColorKey | null;
	backgroundColor?: string;
	isSelected: boolean;
	label: string;
	showTooltip: boolean;
	onColorSelect: (color: TabColorKey | null) => void;
	tabIndex: number;
	flatIndex: number;
	onFocusSwatch: (index: number) => void;
	initialFocusRef?: (el: HTMLElement | null) => void;
};

const Swatch = ({
	colorKey,
	backgroundColor,
	isSelected,
	label,
	showTooltip,
	onColorSelect,
	tabIndex,
	flatIndex,
	onFocusSwatch,
	initialFocusRef,
}: SwatchProps) => {
	const handleFocus = useCallback(() => onFocusSwatch(flatIndex), [flatIndex, onFocusSwatch]);
	const handleClick = useCallback(() => {
		onColorSelect(colorKey);
	}, [colorKey, onColorSelect]);

	// Capture initial values in refs so the ref callback below doesn't need
	// them as dependencies — the callback must only run on mount so that
	// React re-renders don't override the DOM tabIndex set by updateTabIndex.
	const initialTabIndexRef = useRef(tabIndex);
	const initialFocusRefRef = useRef(initialFocusRef);

	// Set tabIndex imperatively on mount so React re-renders don't override
	// the DOM changes made by updateTabIndex during keyboard navigation.
	const setRef = useCallback((el: HTMLElement | null) => {
		if (el) {
			el.tabIndex = initialTabIndexRef.current;
		}
		const focusRef = initialFocusRefRef.current;
		if (focusRef) {
			if (typeof focusRef === 'function') {
				focusRef(el);
			} else {
				(focusRef as React.MutableRefObject<HTMLElement | null>).current = el;
			}
		}
	}, []);

	const swatch = (
		<Pressable
			ref={setRef}
			onClick={handleClick}
			onFocus={handleFocus}
			aria-label={label}
			role="radio"
			aria-checked={isSelected ? 'true' : 'false'}
			style={
				{
					'--tab-color-swatch-background-color': backgroundColor,
				} as TabColorSwatchCustomProperties
			}
			xcss={cx(styles.swatch, backgroundColor ? styles.swatchFromCatalog : styles.swatchNoColor)}
		>
			{isSelected && <CheckMarkIcon label="" color={token('color.icon')} />}
		</Pressable>
	);

	return showTooltip ? <Tooltip content={label}>{swatch}</Tooltip> : swatch;
};

Swatch.displayName = 'Swatch';

/**
 * Helper function to get the localized swatch label.
 * Handles the no-color case and appends the selected state when needed.
 */
function getSwatchLabel(
	colorKey: TabColorKey | null,
	isSelected: boolean,
	formatMessage: ReturnType<typeof useIntl>['formatMessage'],
	labelMessage: MessageDescriptor | undefined,
): string {
	if (colorKey === null) {
		return formatMessage(i18n.noColor);
	}
	const label = labelMessage ? formatMessage(labelMessage) : colorKey;
	if (isSelected) {
		return formatMessage(i18n.selectedSuffix, {
			colorLabel: label,
		});
	}
	return label;
}

export const TabColorPicker: {
	(props: TabColorPickerProps): React.JSX.Element;
	displayName: string;
} = ({
	currentColor,
	onColorSelect,
	setInitialFocusRef,
	palette,
	applyToAll,
	onApplyToAllChange,
}: TabColorPickerProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { appearanceMode, columnCount, showSwatchTooltips, swatches: colorSwatches } = palette;
	const totalItems = colorSwatches.length;
	const initialFocusIndex = currentColor !== undefined ? colorSwatches.indexOf(currentColor) : 0;
	const initialFocused = initialFocusIndex >= 0 ? initialFocusIndex : 0;
	const focusedIndexRef = useRef(initialFocused);

	// Update tabIndex directly on DOM elements to avoid re-rendering all swatches
	const updateTabIndex = useCallback((prevIndex: number, nextIndex: number) => {
		const swatches = containerRef.current?.querySelectorAll('[role="radio"]');
		if (swatches) {
			(swatches[prevIndex] as HTMLElement | undefined)?.setAttribute('tabindex', '-1');
			(swatches[nextIndex] as HTMLElement | undefined)?.setAttribute('tabindex', '0');
		}
		focusedIndexRef.current = nextIndex;
	}, []);

	const handleFocusSwatch = useCallback(
		(index: number) => {
			updateTabIndex(focusedIndexRef.current, index);
		},
		[updateTabIndex],
	);
	const containerRef = useRef<HTMLDivElement>(null);

	// Keep keyboard navigation math over the flat swatch list.
	const handleGridKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLDivElement>) => {
			let newIndex: number;

			switch (e.key) {
				case 'ArrowRight':
					e.preventDefault();
					e.stopPropagation();
					e.nativeEvent.stopImmediatePropagation();
					newIndex = (focusedIndexRef.current + 1) % totalItems;
					break;
				case 'ArrowLeft':
					e.preventDefault();
					e.stopPropagation();
					e.nativeEvent.stopImmediatePropagation();
					newIndex = (focusedIndexRef.current - 1 + totalItems) % totalItems;
					break;
				case 'ArrowDown':
					e.preventDefault();
					e.stopPropagation();
					e.nativeEvent.stopImmediatePropagation();
					newIndex = (focusedIndexRef.current + columnCount) % totalItems;
					break;
				case 'ArrowUp':
					e.preventDefault();
					e.stopPropagation();
					e.nativeEvent.stopImmediatePropagation();
					newIndex = (focusedIndexRef.current - columnCount + totalItems) % totalItems;
					break;
				case 'Home':
					e.preventDefault();
					e.stopPropagation();
					e.nativeEvent.stopImmediatePropagation();
					newIndex = 0;
					break;
				case 'End':
					e.preventDefault();
					e.stopPropagation();
					e.nativeEvent.stopImmediatePropagation();
					newIndex = totalItems - 1;
					break;
				default:
					return;
			}

			updateTabIndex(focusedIndexRef.current, newIndex);
			// Focus the corresponding swatch element — scoped to this picker's container
			const swatchElements = containerRef.current?.querySelectorAll('[role="radio"]');
			(swatchElements?.[newIndex] as HTMLElement)?.focus();
		},
		[columnCount, totalItems, updateTabIndex],
	);

	return (
		<>
			<Box
				ref={containerRef}
				role="radiogroup"
				aria-label={formatMessage(i18n.colorPickerLabel)}
				onKeyDown={handleGridKeyDown}
				style={
					{
						'--tab-color-picker-column-count': columnCount,
					} as TabColorPickerGridCustomProperties
				}
				xcss={styles.grid}
			>
				{colorSwatches.map((colorKey, flatIndex) => {
					const isSelected =
						colorKey === currentColor || (colorKey === null && currentColor === undefined);
					const appearance = colorKey ? getTabColorAppearance(colorKey, appearanceMode) : undefined;
					const label = getSwatchLabel(
						colorKey,
						isSelected,
						formatMessage,
						appearance?.swatch.labelMessage,
					);

					return (
						<Swatch
							key={`${flatIndex}-${colorKey ?? 'none'}`}
							colorKey={colorKey}
							backgroundColor={appearance?.swatch.backgroundColor}
							isSelected={isSelected}
							label={label}
							showTooltip={showSwatchTooltips}
							onColorSelect={onColorSelect}
							tabIndex={flatIndex === initialFocused ? 0 : -1}
							flatIndex={flatIndex}
							onFocusSwatch={handleFocusSwatch}
							initialFocusRef={flatIndex === initialFocused ? setInitialFocusRef : undefined}
						/>
					);
				})}
			</Box>
			{onApplyToAllChange && (
				<Inline
					space="space.050"
					alignBlock="center"
					spread="space-between"
					xcss={styles.actionsDivider}
				>
					<Text color="color.text.subtle">{formatMessage(i18n.applyToAll)}</Text>
					<Toggle
						label={formatMessage(i18n.applyToAll)}
						isChecked={applyToAll}
						onChange={(e) => {
							e.stopPropagation();
							onApplyToAllChange(e.currentTarget.checked);
						}}
					/>
				</Inline>
			)}
		</>
	);
};

TabColorPicker.displayName = 'TabColorPicker';
